/**
 * RowGeneratorPanel.jsx - Professional Row Generator for Seat Inventory
 * Elvis Aloha Collection
 * 
 * Implements the 2-control-seat workflow:
 * 1. Place first verified control seat
 * 2. Place second verified control seat
 * 3. System calculates offset
 * 4. Preview remaining seats
 * 5. Add aisle breaks
 * 6. Confirm to create Draft records
 */

import React, { useState, useEffect, useMemo } from 'react';

const RowGeneratorPanel = ({
  isOpen,
  onClose,
  chartId,
  section,
  template,
  controlSeat1,
  controlSeat2,
  onPreview,
  onConfirm,
  onCancel,
  previewData,
  isLoading = false
}) => {
  const [rowNumber, setRowNumber] = useState('1');
  const [firstSeat, setFirstSeat] = useState('14');
  const [lastSeat, setLastSeat] = useState('1');
  const [numberingDirection, setNumberingDirection] = useState('-1'); // -1 = decreasing
  const [rotation, setRotation] = useState('0');
  const [rowType, setRowType] = useState('straight');
  const [aisleBreaks, setAisleBreaks] = useState([]);
  const [aisleGap, setAisleGap] = useState('10');
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);

  // Calculate offset from control seats
  const controlSeatOffset = useMemo(() => {
    if (!controlSeat1 || !controlSeat2) return null;
    return {
      x: controlSeat2.native_center_x - controlSeat1.native_center_x,
      y: controlSeat2.native_center_y - controlSeat1.native_center_y
    };
  }, [controlSeat1, controlSeat2]);

  // Determine seat count
  const seatCount = useMemo(() => {
    const start = parseInt(firstSeat) || 0;
    const end = parseInt(lastSeat) || 0;
    return Math.abs(start - end) + 1;
  }, [firstSeat, lastSeat]);

  // Reset when panel opens
  useEffect(() => {
    if (isOpen) {
      setIsPreviewMode(false);
      setValidationErrors([]);
    }
  }, [isOpen]);

  // Validate inputs
  const validate = () => {
    const errors = [];
    
    if (!controlSeat1) {
      errors.push('Place the first control seat (Seat 14)');
    }
    if (!controlSeat2) {
      errors.push('Place the second control seat (Seat 13)');
    }
    if (!rowNumber) {
      errors.push('Row number is required');
    }
    if (!firstSeat || !lastSeat) {
      errors.push('First and last seat numbers are required');
    }
    if (seatCount < 2) {
      errors.push('At least 2 seats required for row generation');
    }
    if (seatCount > 50) {
      errors.push('Maximum 50 seats per row');
    }
    
    setValidationErrors(errors);
    return errors.length === 0;
  };

  const handlePreview = () => {
    if (!validate()) return;
    
    const previewRequest = {
      template_id: template?.id,
      chart_id: chartId,
      section: section,
      row: rowNumber,
      control_seat_1: {
        seat: controlSeat1.seat || firstSeat,
        native_x: controlSeat1.native_center_x,
        native_y: controlSeat1.native_center_y
      },
      control_seat_2: {
        seat: controlSeat2.seat || String(parseInt(firstSeat) + parseInt(numberingDirection)),
        native_x: controlSeat2.native_center_x,
        native_y: controlSeat2.native_center_y
      },
      first_seat_number: parseInt(firstSeat),
      last_seat_number: parseInt(lastSeat),
      numbering_direction: parseInt(numberingDirection),
      row_type: rowType,
      aisle_breaks: aisleBreaks.map(Number),
      aisle_gap: parseFloat(aisleGap),
      constant_rotation: parseFloat(rotation),
      preview_only: true
    };
    
    setIsPreviewMode(true);
    onPreview?.(previewRequest);
  };

  const handleCancelPreview = () => {
    setIsPreviewMode(false);
    onCancel?.();
  };

  const handleConfirm = () => {
    if (!validate()) return;
    
    const confirmRequest = {
      template_id: template?.id,
      chart_id: chartId,
      section: section,
      row: rowNumber,
      control_seat_1: {
        seat: firstSeat,
        native_x: controlSeat1.native_center_x,
        native_y: controlSeat1.native_center_y
      },
      control_seat_2: {
        seat: String(parseInt(firstSeat) + parseInt(numberingDirection)),
        native_x: controlSeat2.native_center_x,
        native_y: controlSeat2.native_center_y
      },
      first_seat_number: parseInt(firstSeat),
      last_seat_number: parseInt(lastSeat),
      numbering_direction: parseInt(numberingDirection),
      row_type: rowType,
      aisle_breaks: aisleBreaks.map(Number),
      aisle_gap: parseFloat(aisleGap),
      constant_rotation: parseFloat(rotation),
      preview_only: false
    };
    
    onConfirm?.(confirmRequest);
  };

  const addAisleBreak = (seatNum) => {
    if (!aisleBreaks.includes(seatNum)) {
      setAisleBreaks([...aisleBreaks, seatNum].sort((a, b) => b - a));
    }
  };

  const removeAisleBreak = (seatNum) => {
    setAisleBreaks(aisleBreaks.filter(n => n !== seatNum));
  };

  if (!isOpen) return null;

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-4 space-y-4" data-testid="row-generator-panel">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-gray-900">Row Generator</h3>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Control Seats Status */}
      <div className="bg-gray-50 rounded-lg p-3 space-y-2">
        <div className="text-xs font-medium text-gray-700 uppercase tracking-wide">Control Seats</div>
        <div className="grid grid-cols-2 gap-2">
          <div className={`p-2 rounded text-sm ${controlSeat1 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
            {controlSeat1 ? (
              <>
                <span className="font-medium">Seat 1:</span> ({controlSeat1.native_center_x?.toFixed(0)}, {controlSeat1.native_center_y?.toFixed(0)})
              </>
            ) : (
              'Place first control seat'
            )}
          </div>
          <div className={`p-2 rounded text-sm ${controlSeat2 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
            {controlSeat2 ? (
              <>
                <span className="font-medium">Seat 2:</span> ({controlSeat2.native_center_x?.toFixed(0)}, {controlSeat2.native_center_y?.toFixed(0)})
              </>
            ) : (
              'Place second control seat'
            )}
          </div>
        </div>
        {controlSeatOffset && (
          <div className="text-xs text-gray-600">
            Offset: X={controlSeatOffset.x.toFixed(1)}, Y={controlSeatOffset.y.toFixed(1)}
          </div>
        )}
      </div>

      {/* Row Configuration */}
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs text-gray-600 mb-1">Section</label>
            <input
              type="text"
              value={section || 'BB'}
              disabled
              className="w-full px-3 py-1.5 bg-gray-100 border border-gray-300 rounded text-sm"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Row</label>
            <input
              type="text"
              value={rowNumber}
              onChange={(e) => setRowNumber(e.target.value)}
              className="w-full px-3 py-1.5 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-blue-500"
              data-testid="row-number-input"
            />
          </div>
        </div>

        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="block text-xs text-gray-600 mb-1">First Seat</label>
            <input
              type="number"
              value={firstSeat}
              onChange={(e) => setFirstSeat(e.target.value)}
              className="w-full px-3 py-1.5 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-blue-500"
              data-testid="first-seat-input"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Last Seat</label>
            <input
              type="number"
              value={lastSeat}
              onChange={(e) => setLastSeat(e.target.value)}
              className="w-full px-3 py-1.5 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-blue-500"
              data-testid="last-seat-input"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Direction</label>
            <select
              value={numberingDirection}
              onChange={(e) => setNumberingDirection(e.target.value)}
              className="w-full px-3 py-1.5 border border-gray-300 rounded text-sm"
            >
              <option value="-1">14→1</option>
              <option value="1">1→14</option>
            </select>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs text-gray-600 mb-1">Rotation (°)</label>
            <input
              type="number"
              value={rotation}
              onChange={(e) => setRotation(e.target.value)}
              className="w-full px-3 py-1.5 border border-gray-300 rounded text-sm"
              step="0.5"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Row Type</label>
            <select
              value={rowType}
              onChange={(e) => setRowType(e.target.value)}
              className="w-full px-3 py-1.5 border border-gray-300 rounded text-sm"
            >
              <option value="straight">Straight</option>
              <option value="angled">Angled</option>
              <option value="curved">Curved</option>
            </select>
          </div>
        </div>
      </div>

      {/* Aisle Breaks */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <label className="text-xs text-gray-600">Aisle Breaks</label>
          <input
            type="number"
            placeholder="Add after seat..."
            className="w-24 px-2 py-1 text-xs border border-gray-300 rounded"
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                addAisleBreak(parseInt(e.target.value));
                e.target.value = '';
              }
            }}
          />
        </div>
        {aisleBreaks.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {aisleBreaks.map(seatNum => (
              <span key={seatNum} className="inline-flex items-center px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded">
                After seat {seatNum}
                <button
                  onClick={() => removeAisleBreak(seatNum)}
                  className="ml-1 hover:text-amber-900"
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        )}
        <div>
          <label className="block text-xs text-gray-600 mb-1">Aisle Gap (px)</label>
          <input
            type="number"
            value={aisleGap}
            onChange={(e) => setAisleGap(e.target.value)}
            className="w-20 px-2 py-1 text-sm border border-gray-300 rounded"
          />
        </div>
      </div>

      {/* Validation Errors */}
      {validationErrors.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded p-2">
          <ul className="text-xs text-red-700 list-disc list-inside">
            {validationErrors.map((error, i) => (
              <li key={i}>{error}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Preview Info */}
      {isPreviewMode && previewData && (
        <div className="bg-blue-50 border border-blue-200 rounded p-3">
          <div className="text-sm font-medium text-blue-800">Preview: {previewData.total_seats} seats</div>
          <div className="text-xs text-blue-600 mt-1">
            {previewData.preview_seats?.filter(s => s.is_control_seat).length || 0} control seats + {' '}
            {(previewData.total_seats || 0) - (previewData.preview_seats?.filter(s => s.is_control_seat).length || 0)} generated
          </div>
          <p className="text-xs text-blue-500 mt-2">
            Preview only - no records created yet. Click &ldquo;Confirm&rdquo; to create Draft seats.
          </p>
        </div>
      )}

      {/* Summary */}
      <div className="bg-gray-50 rounded p-2 text-xs text-gray-600">
        Template: {template?.name || 'Arena Standard Seat'} | 
        Seats: {seatCount} | 
        Section: {section}
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-2 pt-2 border-t border-gray-200">
        {!isPreviewMode ? (
          <>
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:text-gray-800 text-sm"
            >
              Cancel
            </button>
            <button
              onClick={handlePreview}
              disabled={!controlSeat1 || !controlSeat2 || isLoading}
              className={`px-4 py-2 rounded text-sm font-medium ${
                controlSeat1 && controlSeat2 && !isLoading
                  ? 'bg-blue-600 text-white hover:bg-blue-700'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
              data-testid="preview-row-btn"
            >
              {isLoading ? 'Loading...' : 'Preview Row'}
            </button>
          </>
        ) : (
          <>
            <button
              onClick={handleCancelPreview}
              className="px-4 py-2 text-gray-600 hover:text-gray-800 text-sm"
              data-testid="cancel-preview-btn"
            >
              Cancel Preview
            </button>
            <button
              onClick={handleConfirm}
              disabled={isLoading}
              className={`px-4 py-2 rounded text-sm font-medium ${
                !isLoading
                  ? 'bg-green-600 text-white hover:bg-green-700'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
              data-testid="confirm-row-btn"
            >
              {isLoading ? 'Creating...' : 'Confirm Generation'}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default RowGeneratorPanel;
